import { type AnyObject } from '@Types';
import transformKeyCase from 'Utils/transformKeyCase';
import { invalidateAllProjectSubmissionTags, type ProjectSubmission } from '../../api';
import {
    type DialogModal,
    type EvaluationModalDataResult,
    type IguanaModel,
    type OcLazyLoad,
    type OpenEvaluationModalCoreParams,
} from './evaluationModal.types';

/**
 * Transforms fetch result data to snake_case format for Iguana models.
 * Creates a default project progress object if none exists in the fetch result.
 */
function transformFetchResultData(
    fetchResult: EvaluationModalDataResult,
    submission: ProjectSubmission,
): {
    learnerProjectData: AnyObject;
    cohortData: AnyObject;
    projectProgressData: AnyObject;
    userData: AnyObject;
    projectGroupMembershipData: AnyObject | null;
} {
    const learnerProjectData = transformKeyCase<AnyObject>(fetchResult.learnerProject, { to: 'snakeCase' });
    const cohortData = transformKeyCase<AnyObject>(fetchResult.cohort, { to: 'snakeCase' });
    const userData = transformKeyCase<AnyObject>(fetchResult.user, { to: 'snakeCase' });
    const projectGroupMembershipData = fetchResult.projectGroupMembership
        ? transformKeyCase<AnyObject>(fetchResult.projectGroupMembership, { to: 'snakeCase' })
        : null;
    const projectProgressData = fetchResult.projectProgress
        ? transformKeyCase<AnyObject>(fetchResult.projectProgress, { to: 'snakeCase' })
        : {
              requirement_identifier: submission.requirementIdentifier,
              status: null,
              id_verified: null,
              historical_project_submissions: [],
          };

    return { learnerProjectData, cohortData, projectProgressData, userData, projectGroupMembershipData };
}

/**
 * Opens the evaluation modal for a project submission.
 * Handles Angular module injection, data transformation, and cache invalidation.
 */
export async function openEvaluationModalCore({
    submission,
    $injector,
    dispatch,
    fetchResult,
    onSuccess,
    onClose,
}: OpenEvaluationModalCoreParams): Promise<void> {
    // Load the Admin module and inject dependencies
    // Errors here will propagate to the calling hook which displays them via error dialog
    try {
        await import('Admin/angularModule');
        const $ocLazyLoad = $injector.get<OcLazyLoad>('$ocLazyLoad');
        $ocLazyLoad.inject('FrontRoyal.Admin');
    } catch (_error) {
        throw new Error('Unable to open evaluation modal: failed to load required modules');
    }

    const DialogModal = $injector.get<DialogModal>('DialogModal');
    const LearnerProject = $injector.get<IguanaModel>('LearnerProject');
    const ProjectProgress = $injector.get<IguanaModel>('ProjectProgress');
    const Cohort = $injector.get<IguanaModel>('Cohort');

    const { learnerProjectData, cohortData, projectProgressData, userData, projectGroupMembershipData } =
        transformFetchResultData(fetchResult, submission);

    const project = LearnerProject.new(learnerProjectData);
    const cohort = Cohort.new(cohortData);
    const projectProgress = ProjectProgress.new(projectProgressData);

    DialogModal.alert({
        title: (project as AnyObject).title as string,
        size: 'large',
        classes: ['project-fields-modal-wide'],
        content: `<project-fields-modal project="project" project-progress="projectProgress" cohort="cohort" user="user" project-group-membership="projectGroupMembership" on-success="onSuccess"></project-fields-modal>`,
        scope: {
            project,
            projectProgress,
            cohort,
            user: userData,
            projectGroupMembership: projectGroupMembershipData,
            onSuccess: () => {
                dispatch(invalidateAllProjectSubmissionTags(submission.id));
                onSuccess?.();
            },
        },
        close: onClose,
    });
}
